import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, interval, Subscription, take } from 'rxjs';
import { UserRole } from '../models/user.model';
import { PendingTimeSync } from '../utils/session-time.util';
import { PatientSessionService } from './patient-session.service';
import { UserService } from './user';

@Injectable({
  providedIn: 'root',
})
export class TimeTrackingService implements OnDestroy {
  private sessionService = inject(PatientSessionService);
  private userService = inject(UserService);
  private router = inject(Router);

  private timeTrackingInterval: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private isTracking = signal(false);
  private pendingTime = new PendingTimeSync(Date.now());
  private failedFlush = false;

  // Exercise route patterns
  private readonly EXERCISE_ROUTES = [
    '/sentence/',
    '/word-level/',
    '/memory-level/',
    '/audio-match/',
    '/audio-memory/',
    '/word-audio-match/',
    '/phoneme-level/',
    '/minimal-pair/',
    '/phoneme-finder/',
    '/comic/',
  ];

  private readonly GAME_ROUTES = [
    '/shape-recognition',
    '/shape-size',
    '/path-game',
  ];

  // Track time every 30 seconds
  private readonly UPDATE_INTERVAL = 10 * 1000; // 10 seconds

  constructor() {
    // Initialize session for patients
    effect(() => {
      const user = this.userService.currentUser();
      if (
        user &&
        (user.role === UserRole.PATIENT || user.role === UserRole.USER)
      ) {
        this.sessionService.getCurrentSession().subscribe({
          next: (session) => {
            // check if sessiondate is already stored in browser session storage
            const sessionDate = sessionStorage.getItem('sessionDate');
            if (!sessionDate) {
              sessionStorage.setItem('sessionDate', session.sessionDate);
              return;
            }
            if (sessionDate !== session.sessionDate) {
              // clear all items in session storage
              sessionStorage.clear();
              sessionStorage.setItem('sessionDate', session.sessionDate);
              return;
            }
          },
        });
      }
    });

    // Watch for time limit reached
    effect(() => {
      const session = this.sessionService.session();
      if (session?.isTotalTimeLimitReached && this.isTracking()) {
        console.debug('Time limit reached, stopping tracking');
        this.stopTracking();
      }
    });

    // Listen to router navigation events
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.handleRouteChange(event.urlAfterRedirects);
        }
      });

    // Check initial route
    this.handleRouteChange(this.router.url);
  }

  private handleRouteChange(url: string) {
    const user = this.userService.currentUser();
    const isPatient =
      user &&
      (user.role === UserRole.PATIENT ||
        (user.role === UserRole.USER && !user.isAdmin));
    const isExerciseRoute = this.isExerciseRoute(url);

    if (isPatient && isExerciseRoute) {
      this.startTracking();
    } else {
      this.stopTracking();
    }
  }

  isExerciseRoute(url: string): boolean {
    return (
      this.EXERCISE_ROUTES.some((pattern) => url.includes(pattern)) ||
      this.GAME_ROUTES.some((pattern) => url.includes(pattern))
    );
  }

  startTracking() {
    if (this.isTracking()) {
      return;
    }

    this.isTracking.set(true);
    this.pendingTime.resetClock(Date.now());

    // Ensure session is initialized
    const session = this.sessionService.session();
    if (!session) {
      this.sessionService.getCurrentSession().subscribe({
        next: () => {
          if (!this.isTracking()) {
            return;
          }
          this.startTrackingInterval();
        },
      });
    } else {
      // Session already exists, start interval
      this.startTrackingInterval();
    }
  }

  private startTrackingInterval() {
    // Start interval to track time
    this.timeTrackingInterval = interval(this.UPDATE_INTERVAL).subscribe(() => {
      this.updateTimeSpent();
    });
  }

  stopTracking() {
    if (!this.isTracking()) {
      return;
    }

    // Final update before stopping
    this.updateTimeSpent();

    if (this.timeTrackingInterval) {
      this.timeTrackingInterval.unsubscribe();
      this.timeTrackingInterval = null;
    }

    this.isTracking.set(false);
  }

  private updateTimeSpent() {
    const elapsedSeconds = this.pendingTime.noteElapsed(Date.now());
    if (elapsedSeconds > 0) {
      this.applyOptimisticTime(elapsedSeconds);
    }
    this.flushPendingTime();
  }

  private applyOptimisticTime(elapsedSeconds: number) {
    const session = this.sessionService.session();
    if (!session) {
      return;
    }

    const newRemaining = Math.max(
      0,
      session.remainingRequiredExercisesTime - elapsedSeconds,
    );
    const newRemainingTotalTime = Math.max(
      0,
      session.remainingTotalTime - elapsedSeconds,
    );
    this.sessionService.session.set({
      ...session,
      timeSpentSeconds: session.timeSpentSeconds + elapsedSeconds,
      remainingRequiredExercisesTime: newRemaining,
      remainingTotalTime: newRemainingTotalTime,
      isRequiredExercisesTimeLimitReached: newRemaining <= 0,
      isTotalTimeLimitReached: newRemainingTotalTime <= 0,
      shouldShowWarning: newRemaining <= 5 * 60 && newRemaining > 0,
    });
  }

  private flushPendingTime() {
    const seconds = this.pendingTime.takeForSend();
    if (seconds <= 0) {
      return;
    }

    this.sessionService
      .updateTimeSpent(seconds)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.failedFlush = false;
          this.pendingTime.finishSend();
          this.flushPendingTime();
        },
        error: (error: { status?: number }) => {
          if (error?.status === 403) {
            this.pendingTime.abandonPending();
            this.markTotalLimitReached();
            return;
          }
          console.error('Error updating time spent:', error);
          this.pendingTime.restoreSend(seconds);
          if (!this.failedFlush) {
            this.failedFlush = true;
            this.flushPendingTime();
            return;
          }
          this.failedFlush = false;
        },
      });
  }

  private markTotalLimitReached() {
    const session = this.sessionService.session();
    if (!session || session.isTotalTimeLimitReached) {
      return;
    }
    this.sessionService.session.set({
      ...session,
      remainingRequiredExercisesTime: 0,
      remainingTotalTime: 0,
      isRequiredExercisesTimeLimitReached: true,
      isTotalTimeLimitReached: true,
      shouldShowWarning: false,
    });
  }

  ngOnDestroy() {
    this.stopTracking();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
